import type { AdminClub } from '../../api/admin'

/**
 * Lecturas derivadas del estado de un club, compartidas por la lista
 * (`AdminClubs`) y la ficha (`AdminClubDetail`) para que las dos digan lo
 * mismo con las mismas palabras y los mismos umbrales.
 *
 * Todo son funciones puras sobre la fila que ya devuelve `GET /admin/clubs`
 * (más `now` inyectable, así los tests no dependen del reloj): el criterio de
 * "vencido / por vencer / lleno / inactivo" vive acá y en ningún otro lado.
 */

/** Ventana en la que un vencimiento de servicio ya se avisa como próximo. */
export const SERVICE_WARN_DAYS = 30
/** A partir de este uso del cupo la fila avisa que está por llenarse. */
export const SEAT_WARN_RATIO = 0.85

export type Tone = 'ok' | 'warn' | 'err' | 'muted'

/** `2 invitaciones` / `1 invitación`. El plural del español no siempre es
 * sufijar "es" (la tilde se cae: invitación → invitaciones), así que los dos
 * casos van explícitos en vez de concatenar. */
export function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

const TAG_CLASS: Record<Tone, string> = {
  ok: 'tag-ok',
  warn: 'tag-warn',
  err: 'tag-err',
  muted: 'tag',
}

export function tagClass(tone: Tone): string {
  return TAG_CLASS[tone]
}

/** Parsea el `YYYY-MM-DD` de `service_until` como fecha LOCAL (el `T00:00:00`
 * evita que `new Date('2026-09-08')` — que es UTC — se corra un día en AR). */
export function parseServiceDate(value: string): Date {
  return new Date(`${value}T00:00:00`)
}

export type ServiceStatus = {
  tone: Tone
  /** Texto corto para el chip de la fila. */
  label: string
  /** Frase completa para el resumen de la ficha / el `title` del chip. */
  detail: string
  /** Días hasta el vencimiento (negativo si ya venció). Null = sin fecha. */
  daysLeft: number | null
}

export function serviceStatus(
  club: Pick<AdminClub, 'service_until' | 'service_active'>,
  now: Date = new Date(),
): ServiceStatus {
  if (!club.service_until) {
    return {
      tone: 'muted',
      label: 'Sin vencimiento',
      detail: 'Servicio indefinido: no hay fecha de corte cargada.',
      daysLeft: null,
    }
  }
  const until = parseServiceDate(club.service_until)
  const fmt = until.toLocaleDateString('es-AR')
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
  const daysLeft = Math.round(
    (until.getTime() - startOfToday.getTime()) / 86_400_000,
  )

  // `service_active` lo calcula el backend (UTC) y manda: es el mismo booleano
  // que le corta el VR a los jugadores. `daysLeft` sólo matiza el aviso.
  if (!club.service_active) {
    return {
      tone: 'err',
      label: 'Vencido',
      detail: `Servicio vencido el ${fmt}. Los jugadores del club no pueden entrar al VR.`,
      daysLeft,
    }
  }
  if (daysLeft <= SERVICE_WARN_DAYS) {
    return {
      tone: 'warn',
      label: daysLeft <= 0 ? 'Vence hoy' : `Vence en ${daysLeft} d`,
      detail: `El servicio vence el ${fmt}.`,
      daysLeft,
    }
  }
  return {
    tone: 'ok',
    label: `Hasta ${fmt}`,
    detail: `Servicio habilitado hasta el ${fmt}.`,
    daysLeft,
  }
}

export type SeatUsage = {
  tone: Tone
  /** `"12 / 20"`, o `"12 / ∞"` cuando el club no tiene cupo. */
  label: string
  detail: string
  /** Lugares libres. Null = sin límite. */
  free: number | null
}

export function seatUsage(
  club: Pick<AdminClub, 'player_count' | 'seat_limit'>,
): SeatUsage {
  const used = club.player_count
  const limit = club.seat_limit
  if (limit == null) {
    return {
      tone: 'muted',
      label: `${used} / ∞`,
      detail: `${used} jugador${used === 1 ? '' : 'es'}, sin cupo de licencias.`,
      free: null,
    }
  }
  const free = limit - used
  const tone: Tone = free <= 0 ? 'err' : used / Math.max(limit, 1) >= SEAT_WARN_RATIO ? 'warn' : 'ok'
  return {
    tone,
    label: `${used} / ${limit}`,
    detail:
      free <= 0
        ? `Cupo lleno (${used}/${limit}): no se pueden dar de alta más jugadores.`
        : `Quedan ${free} licencia${free === 1 ? '' : 's'} de ${limit}.`,
    free,
  }
}

/**
 * "hoy" / "ayer" / "hace N días" para la última sesión; fecha corta cuando
 * pasó más de un mes, que es cuando el número de días deja de decir algo.
 */
export function relativeDay(iso: string | null, now: Date = new Date()): string {
  if (!iso) return 'nunca'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  const days = Math.floor(
    (new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime() -
      new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()) /
      86_400_000,
  )
  if (days <= 0) return 'hoy'
  if (days === 1) return 'ayer'
  if (days <= 30) return `hace ${days} d`
  return d.toLocaleDateString('es-AR')
}

/**
 * Un club "activo" es uno que entrenó en la ventana que reporta el backend
 * (`sessions_7d`). Sin sesiones recientes el chip queda apagado en vez de
 * gritar: no entrenar una semana no es un error, es información.
 */
export function activityTone(club: Pick<AdminClub, 'sessions_7d'>): Tone {
  return club.sessions_7d > 0 ? 'ok' : 'muted'
}

/** Cosas que un operador tendría que ir a resolver. Vacío = club en orden. */
export function clubAlerts(club: AdminClub, now: Date = new Date()): string[] {
  const alerts: string[] = []
  const service = serviceStatus(club, now)
  if (service.tone === 'err' || service.tone === 'warn') alerts.push(service.detail)
  const seats = seatUsage(club)
  if (seats.tone === 'err') alerts.push(seats.detail)
  if (club.staff_count === 0) {
    alerts.push(
      club.staff_invites_pending > 0
        ? `Sin staff todavía: ${plural(club.staff_invites_pending, 'invitación', 'invitaciones')} sin usar.`
        : 'Sin staff: nadie puede entrar al panel del club.',
    )
  } else if (club.staff_pending_terms > 0) {
    alerts.push(
      `${club.staff_pending_terms} de ${club.staff_count} del staff no aceptaron los T&C (el panel les queda bloqueado).`,
    )
  }
  if (club.kit_count === 0) {
    alerts.push('Sin código de club: no se puede entrar desde la tablet ni el visor.')
  } else if (club.kits_active === 0) {
    alerts.push('Todos los códigos de club están desactivados.')
  }
  return alerts
}

/**
 * Cuán urgente es lo que le pasa a un club: 2 = algo ya está roto (servicio
 * vencido, cupo lleno, sin staff, sin código de club), 1 = algo se va a romper
 * (vencimiento próximo, staff que debe los T&C, kits apagados), 0 = en orden.
 * Ordena el bloque "Requieren atención" para que lo roto no quede debajo de
 * lo que todavía tiene margen.
 */
export function clubSeverity(club: AdminClub, now: Date = new Date()): 0 | 1 | 2 {
  if (clubAlerts(club, now).length === 0) return 0
  const blocking =
    serviceStatus(club, now).tone === 'err' ||
    seatUsage(club).tone === 'err' ||
    club.staff_count === 0 ||
    club.kit_count === 0
  return blocking ? 2 : 1
}
